/**
 * Component: SpeechConfigPage
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R10, SPEECH-V1-R11, SPEECH-V1-R16
 *
 * Admin page for the SPEECH-V1 speech configuration a deployment owns:
 * the offered recognition languages, the cardiology/ESC phrase list, and
 * the transcript normalisation rules.
 *
 * Loads the active config from `GET /v1/speech/phrase-config`,
 * lets an authorised admin edit it, and posts the whole blob back to
 * `PUT /v1/speech/phrase-config`. The `Reset to defaults`
 * button calls `POST /v1/speech/phrase-config/reset` to drop
 * back to the seeded built-in cardiology/ESC term list.
 *
 * Three lists, edited together because they share one config record and
 * therefore one write:
 *   1. Languages (SPEECH-V1-R16) — the locales this deployment offers
 *      for speech recognition, and which of them applies when a user
 *      has made no selection. An empty list means the deployment's
 *      environment values apply, so a deployment that never visits this
 *      page behaves exactly as it did before the setting existed.
 *      Chosen from a catalogue so an admin need not recall BCP-47 tags,
 *      with direct entry for a locale the catalogue does not carry.
 *   2. Phrase-list terms — words/phrases fed to Azure's
 *      streaming recognizer to bias recognition toward
 *      domain terminology. An empty list means no bias is
 *      applied (the streaming session behaves exactly as it
 *      does without this feature — a clean no-op, not an
 *      error state).
 *   3. Normalisation rules — pattern/replacement pairs
 *      rewriting recognized spoken-form variants (e.g.
 *      "score two") into their canonical written form (e.g.
 *      "SCORE2") on the final transcript only.
 *
 * The terms list shows a live {count}/500 counter and
 * disables "Add term" at the cap, matching the backend's
 * SPEECH_PHRASE_LIST_MAX_TERMS validation limit.
 *
 * Accessibility: WCAG 2.1 AA compliant (label associations,
 * keyboard-accessible add/remove buttons, accessible name
 * for icon-only buttons).
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Loader2,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Mic,
  AlertCircle,
  Check,
} from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import {
  DEFAULT_SPEECH_PHRASE_CONFIG,
  SPEECH_PHRASE_LIST_MAX_TERMS,
  type CatalogueLocale,
  type ConfiguredLanguage,
  type NormalizationRule,
  type SpeechPhraseConfig,
  type SpeechPhraseConfigApiResponse,
  type SpeechPhraseConfigUpdateRequest,
} from '@/types/speechPhraseConfig';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface FormState {
  languages: ConfiguredLanguage[];
  default_language: string | null;
  terms: string[];
  normalizationRules: NormalizationRule[];
}

function configToFormState(config: SpeechPhraseConfig): FormState {
  return {
    // Tolerant of a config blob predating the language fields, so the page
    // still loads against a record written before this setting existed.
    languages: (config.languages ?? []).map((l) => ({ ...l })),
    default_language: config.default_language ?? null,
    terms: [...config.terms],
    normalizationRules: config.normalization_rules.map((rule) => ({ ...rule })),
  };
}

/**
 * Convert form state back to the API config blob.
 *
 * Drops empty term/pattern/replacement entries silently — an
 * admin who wants to "delete" a row uses the row remove
 * button rather than blanking it out.
 */
function formStateToConfig(state: FormState): SpeechPhraseConfig {
  const terms = state.terms.map((t) => t.trim()).filter((t) => t.length > 0);

  const normalizationRules = state.normalizationRules
    .map((rule) => ({
      pattern: rule.pattern.trim(),
      replacement: rule.replacement.trim(),
    }))
    .filter((rule) => rule.pattern.length > 0 && rule.replacement.length > 0);

  return {
    languages: state.languages.map((l) => ({ ...l })),
    default_language: state.default_language,
    terms,
    normalization_rules: normalizationRules,
  };
}

export const SpeechConfigPage: React.FC = () => {
  const [formState, setFormState] = useState<FormState>(() =>
    configToFormState(DEFAULT_SPEECH_PHRASE_CONFIG),
  );
  const [meta, setMeta] = useState<{
    version: number;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // SPEECH-V1-R16: the locales an admin may pick from. Fetched separately
  // from the config because it describes what the recognition service
  // supports, not what this deployment has chosen.
  const [catalogue, setCatalogue] = useState<CatalogueLocale[]>([]);
  const [customCode, setCustomCode] = useState<string>('');
  const [customLabel, setCustomLabel] = useState<string>('');

  const loadCatalogue = useCallback(async () => {
    try {
      const res = await api.get<CatalogueLocale[]>('/speech/locale-catalogue');
      setCatalogue(Array.isArray(res.data) ? res.data : []);
    } catch {
      // Non-fatal: direct entry still works, so an admin is never blocked
      // from configuring a language because the catalogue failed to load.
    }
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<SpeechPhraseConfigApiResponse>(
        '/speech/phrase-config',
      );
      setFormState(configToFormState(res.data.config));
      setMeta({
        version: res.data.version,
        updatedAt: res.data.updated_at,
        updatedBy: res.data.updated_by,
      });
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
    void loadCatalogue();
  }, [loadConfig, loadCatalogue]);

  const handleLanguageToggle = useCallback((locale: CatalogueLocale) => {
    setFormState((prev) => {
      const present = prev.languages.some((l) => l.code === locale.code);
      const languages = present
        ? prev.languages.filter((l) => l.code !== locale.code)
        : [...prev.languages, { code: locale.code, label: locale.label }];
      // Removing the language that is currently the default would leave a
      // default the picker never offers, which the backend rejects - clear
      // it here so the admin sees the consequence immediately rather than
      // on save.
      const defaultLanguage =
        prev.default_language &&
        !languages.some((l) => l.code === prev.default_language)
          ? null
          : prev.default_language;
      return { ...prev, languages, default_language: defaultLanguage };
    });
  }, []);

  const handleCustomLanguageAdd = useCallback(() => {
    const code = customCode.trim();
    const label = customLabel.trim() || code;
    if (!code) return;
    setFormState((prev) => {
      if (prev.languages.some((l) => l.code === code)) return prev;
      return { ...prev, languages: [...prev.languages, { code, label }] };
    });
    setCustomCode('');
    setCustomLabel('');
  }, [customCode, customLabel]);

  const handleDefaultLanguageChange = useCallback((code: string) => {
    setFormState((prev) => ({
      ...prev,
      default_language: code === '' ? null : code,
    }));
  }, []);

  const handleTermAdd = useCallback(() => {
    setFormState((prev) => {
      if (prev.terms.length >= SPEECH_PHRASE_LIST_MAX_TERMS) return prev;
      return { ...prev, terms: [...prev.terms, ''] };
    });
  }, []);

  const handleTermRemove = useCallback((index: number) => {
    setFormState((prev) => ({
      ...prev,
      terms: prev.terms.filter((_, i) => i !== index),
    }));
  }, []);

  const handleTermChange = useCallback((index: number, value: string) => {
    setFormState((prev) => ({
      ...prev,
      terms: prev.terms.map((t, i) => (i === index ? value : t)),
    }));
  }, []);

  const handleRuleAdd = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      normalizationRules: [
        ...prev.normalizationRules,
        { pattern: '', replacement: '' },
      ],
    }));
  }, []);

  const handleRuleRemove = useCallback((index: number) => {
    setFormState((prev) => ({
      ...prev,
      normalizationRules: prev.normalizationRules.filter((_, i) => i !== index),
    }));
  }, []);

  const handleRuleChange = useCallback(
    (index: number, field: 'pattern' | 'replacement', value: string) => {
      setFormState((prev) => ({
        ...prev,
        normalizationRules: prev.normalizationRules.map((rule, i) =>
          i === index ? { ...rule, [field]: value } : rule,
        ),
      }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    const config = formStateToConfig(formState);

    try {
      const payload: SpeechPhraseConfigUpdateRequest = { config };
      const res = await api.put<SpeechPhraseConfigApiResponse>(
        '/speech/phrase-config',
        payload,
      );
      setFormState(configToFormState(res.data.config));
      setMeta({
        version: res.data.version,
        updatedAt: res.data.updated_at,
        updatedBy: res.data.updated_by,
      });
      setSuccessMessage(
        `Speech phrase-list configuration saved (version ${res.data.version}).`,
      );
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }, [formState]);

  const handleReset = useCallback(async () => {
    setResetting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await api.post<SpeechPhraseConfigApiResponse>(
        '/speech/phrase-config/reset',
      );
      setFormState(configToFormState(res.data.config));
      setMeta({
        version: res.data.version,
        updatedAt: res.data.updated_at,
        updatedBy: res.data.updated_by,
      });
      setSuccessMessage(
        `Speech phrase-list configuration reset to defaults (version ${res.data.version}).`,
      );
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setResetting(false);
    }
  }, []);

  const termCount = formState.terms.length;
  const atTermCap = termCount >= SPEECH_PHRASE_LIST_MAX_TERMS;

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">
            Loading speech phrase-list configuration…
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Mic className="h-7 w-7" aria-hidden="true" />
            Speech Phrase-List Configuration
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure the cardiology/ESC domain terms used to bias the streaming
            speech-to-text recognizer, and the normalisation rules that rewrite
            recognized spoken-form variants to their canonical written form.
            Changes take effect on the next streaming voice-input session — no
            deployment required.
          </p>
          {meta && (
            <p className="text-sm text-muted-foreground mt-2">
              Current version: <strong>{meta.version}</strong> · last updated by{' '}
              <strong>{meta.updatedBy}</strong> at{' '}
              <time dateTime={meta.updatedAt}>
                {new Date(meta.updatedAt).toLocaleString()}
              </time>
            </p>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert>
          <Check className="h-4 w-4" />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recognition languages</CardTitle>
          <CardDescription>
            The languages this deployment offers for voice input, and which one
            applies when a user has not chosen. Leave the list empty to use the
            languages configured in the deployment&apos;s environment instead.
            Changing this takes effect immediately — no redeploy.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="speech-default-language">Default language</Label>
            <select
              id="speech-default-language"
              className="flex h-9 w-full max-w-sm rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={formState.default_language ?? ''}
              onChange={(e) => handleDefaultLanguageChange(e.target.value)}
              disabled={formState.languages.length === 0}
            >
              <option value="">
                Use the deployment&apos;s configured default
              </option>
              {formState.languages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.label} ({language.code})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Offered languages</p>
            {formState.languages.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                None selected — the deployment&apos;s environment values apply.
              </p>
            )}
            <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
              {catalogue.map((locale) => {
                const checked = formState.languages.some(
                  (l) => l.code === locale.code,
                );
                return (
                  <label
                    key={locale.code}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleLanguageToggle(locale)}
                      className="h-4 w-4"
                    />
                    <span>{locale.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {locale.code}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* SPEECH-V1-R16: direct entry, so a deployment is never blocked by
              the catalogue lagging the recognition service. */}
          <div className="space-y-2 border-t pt-3">
            <p className="text-sm font-medium">Add a locale not listed above</p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label="Language code"
                placeholder="e.g. cy-GB"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value)}
                className="w-40"
              />
              <Input
                aria-label="Language label"
                placeholder="e.g. Cymraeg"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                className="w-56"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCustomLanguageAdd}
                disabled={!customCode.trim()}
              >
                <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                Add
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              A code entered here is not checked against the recognition
              service. A mistyped code will not be recognised and users
              selecting it will fall back to the default.
            </p>
          </div>

          {/* Anything configured that the catalogue does not carry - shown so
              a directly-entered locale is visible and removable, not just
              invisible state. */}
          {formState.languages.filter(
            (l) => !catalogue.some((c) => c.code === l.code),
          ).length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Directly-entered locales</p>
              {formState.languages
                .filter((l) => !catalogue.some((c) => c.code === l.code))
                .map((language) => (
                  <div
                    key={language.code}
                    className="flex items-center gap-2 text-sm"
                  >
                    <span className="flex-1">
                      {language.label}{' '}
                      <span className="text-xs text-muted-foreground">
                        {language.code}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${language.label}`}
                      onClick={() =>
                        handleLanguageToggle({
                          code: language.code,
                          label: language.label,
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Phrase-list terms</CardTitle>
          <CardDescription>
            Cardiology/ESC domain terms (e.g. LVEF, SCORE2, CHA2DS2-VASc) fed to
            the streaming recognizer so it is biased toward recognising them
            correctly. An empty list means no bias is applied — the streaming
            session behaves exactly as it does without this feature.{' '}
            <span className={atTermCap ? 'font-medium text-destructive' : ''}>
              {termCount}/{SPEECH_PHRASE_LIST_MAX_TERMS} terms
            </span>
            .
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {formState.terms.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No phrase-list terms set.
            </p>
          )}
          {formState.terms.map((term, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                aria-label={`Phrase-list term ${index + 1}`}
                placeholder="e.g. CHA2DS2-VASc"
                value={term}
                onChange={(e) => handleTermChange(index, e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove term ${index + 1}`}
                onClick={() => handleTermRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleTermAdd}
            disabled={atTermCap}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add term
          </Button>
          {atTermCap && (
            <p className="text-sm text-destructive">
              Maximum of {SPEECH_PHRASE_LIST_MAX_TERMS} terms reached. Remove a
              term to add another.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Normalisation rules</CardTitle>
          <CardDescription>
            Spoken-form-to-canonical-form substitution pairs applied to the
            final transcript only (never to the live interim display). Matching
            is case-insensitive and longest-pattern-first.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {formState.normalizationRules.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No normalisation rules set.
            </p>
          )}
          {formState.normalizationRules.map((rule, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                aria-label={`Normalisation pattern ${index + 1}`}
                placeholder="pattern (e.g. score two)"
                value={rule.pattern}
                onChange={(e) => handleRuleChange(index, 'pattern', e.target.value)}
                className="flex-1"
              />
              <Input
                aria-label={`Normalisation replacement ${index + 1}`}
                placeholder="replacement (e.g. SCORE2)"
                value={rule.replacement}
                onChange={(e) =>
                  handleRuleChange(index, 'replacement', e.target.value)
                }
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove normalisation rule ${index + 1}`}
                onClick={() => handleRuleRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={handleRuleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add normalisation rule
          </Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={() => void handleSave()} disabled={saving || resetting}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save changes
        </Button>
        <Button
          variant="outline"
          onClick={() => void handleReset()}
          disabled={saving || resetting}
        >
          {resetting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4 mr-2" />
          )}
          Reset to defaults
        </Button>
      </div>
    </div>
  );
};
